import { computed, Injectable, signal } from '@angular/core';
import type { IInjectablesConfig } from '@readium/navigator';
import { debugLog } from '../../../helpers/debugLog';

@Injectable({
  providedIn: 'root',
})
export class CustomFontsService {
  private readonly fonts: { fontFamily: string; href: string; blobUrl?: string }[] = [
    {
      fontFamily: 'OpenDyslexic',
      href: '/OpenDyslexic/OpenDyslexic3-Regular.ttf',
    },
    {
      fontFamily: 'OpenDyslexic-Bold',
      href: '/OpenDyslexic/OpenDyslexic3-Bold.ttf',
    },

    {
      fontFamily: 'Roboto',
      href: '/Roboto/Roboto-Regular.ttf',
    },
    {
      fontFamily: 'Roboto-Bold',
      href: '/Roboto/Roboto-Bold.ttf',
    },

    {
      fontFamily: 'Lato',
      href: '/Lato/Lato-Regular.ttf',
    },
    {
      fontFamily: 'Lato-Bold',
      href: '/Lato/Lato-Bold.ttf',
    },

    {
      fontFamily: 'NotoSansMono',
      href: '/NotoSansMono/NotoSansMono-Regular.ttf',
    },
    {
      fontFamily: 'NotoSansMono-Bold',
      href: '/NotoSansMono/NotoSansMono-Bold.ttf',
    },

    {
      fontFamily: 'Tinos',
      href: '/Tinos/Tinos-Regular.ttf',
    },
    {
      fontFamily: 'Tinos-Bold',
      href: '/Tinos/Tinos-Bold.ttf',
    },

    {
      fontFamily: 'NotoSans',
      href: '/NotoSans/NotoSans-Regular.ttf',
    },
    {
      fontFamily: 'NotoSans-Bold',
      href: '/NotoSans/NotoSans-Bold.ttf',
    },
  ];
  private readonly injectablesConfigSignal = signal<IInjectablesConfig | undefined>(undefined);

  public readonly fontsToShow = this.fonts
    .filter((font) => !font.fontFamily.includes('Bold'))
    .map((font) => font.fontFamily);

  public readonly injectablesConfig = this.injectablesConfigSignal.asReadonly();
  public readonly fontsLoaded = computed(() => this.injectablesConfigSignal() !== undefined);

  constructor() {
    this.loadCustomFonts().then(() => {
      this.injectablesConfigSignal.set(this.buildInjectablesConfig());
    });
  }

  async loadCustomFonts(): Promise<void> {
    const results = await Promise.allSettled(
      this.fonts.map(async (font) => {
        const res = await fetch(`fonts${font.href}`, { cache: 'force-cache' });
        if (!res.ok) throw new Error(`Font ${font.href} not available (${res.status})`);
        const blob = await res.blob();
        return { fontFamily: font.fontFamily, blobUrl: URL.createObjectURL(blob) };
      }),
    );
    // A missing font must not keep the reader from opening: it falls back to the book fonts.
    const loadedFonts = results.flatMap((result) => {
      if (result.status === 'fulfilled') return [result.value];
      debugLog('Custom font not loaded', result.reason);
      return [];
    });
    loadedFonts.forEach((loadedFont) => {
      const font = this.fonts.find((f) => f.fontFamily === loadedFont.fontFamily);
      if (font) {
        font.blobUrl = loadedFont.blobUrl;
      }
    });
  }

  private buildInjectablesConfig(): IInjectablesConfig {
    const cssText = this.fonts
      .filter((font) => font.blobUrl)
      .map((font) => `@font-face {font-family: '${font.fontFamily}';src: url(${font.blobUrl});}`)
      .join('\n');

    return {
      rules: [
        {
          resources: [/.*/],
          prepend: [
            {
              as: 'link',
              rel: 'stylesheet',
              target: 'head',
              blob: new Blob([cssText], { type: 'text/css' }),
            },
          ],
        },
      ],
    };
  }
}
